import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDateShort } from "@/lib/format";
import { MAINT_STATUS, MAINT_TYPES } from "@/lib/fleetConstants";
import { FieldSelect } from "@/components/ui/field-select";
import { MaintenanceDialog } from "@/pages/FlotteDetail";
import { Wrench, Plus, Loader2 } from "lucide-react";

export default function Maintenance() {
  const { orgId, can } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["maintenance-list", orgId, status],
    queryFn: async () => (await api.get("/api/maintenance", { params: { organization_id: orgId, status: status || undefined } })).data,
    enabled: !!orgId,
  });
  const { data: fleet } = useQuery({
    queryKey: ["fleet-min", orgId],
    queryFn: async () => (await api.get("/api/fleet", { params: { organization_id: orgId } })).data,
    enabled: !!orgId && open,
  });
  const rows = data?.maintenances || [];
  const currency = data?.currency || "MAD";

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="maintenance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Maintenance</h1>
        {can("maintenance.manage") && <button onClick={() => setOpen(true)} data-testid="new-maintenance-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouvelle maintenance</button>}
      </div>
      <FieldSelect testId="maintenance-filter-status" className="w-44" value={status || "ALL"} onChange={(v) => setStatus(v === "ALL" ? "" : v)} options={[{ value: "ALL", label: "Tout statut" }, ...Object.entries(MAINT_STATUS).map(([k, v]) => ({ value: k, label: v.label }))]} />

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid="maintenance-empty"><Wrench className="mx-auto h-9 w-9 text-slate-300" /><p className="mt-3 text-base font-semibold text-slate-700">Aucune maintenance planifiée</p><p className="mt-1 text-sm text-slate-400">Planifiez l'entretien de vos ressources pour garder votre flotte opérationnelle.</p>{can("maintenance.manage") && <button onClick={() => setOpen(true)} data-testid="maintenance-empty-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouvelle maintenance</button>}</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="maintenance-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr>{["Référence", "Ressource", "Titre", "Type", "Coût", "Prévu", "Statut"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((mt) => (
                <tr key={mt.id} onClick={() => nav(`/flotte/${mt.resource_id}`)} data-testid={`maintenance-row-${mt.id}`} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-700">{mt.maintenance_reference}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{mt.resource_reference}</td>
                  <td className="px-4 py-3 text-slate-700">{mt.title}</td>
                  <td className="px-4 py-3 text-slate-500">{MAINT_TYPES[mt.type]}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{mt.cost_amount_cents ? formatMoney(mt.cost_amount_cents, currency) : "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{mt.scheduled_date ? formatDateShort(mt.scheduled_date) : "—"}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${MAINT_STATUS[mt.status].badge}`}>{MAINT_STATUS[mt.status].label}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <MaintenanceDialog open={open} onOpenChange={setOpen} resources={fleet?.resources} />
    </div>
  );
}
